import { useEffect } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import type {
  LeaderboardRank,
  LiveLotFeedSubscription,
} from '../graphql/types';
import { LiveLotFeedDocument, useLotQuery, useMeQuery } from '../graphql/types';
import { trackProductViewed } from '../utils/analytics';
import { client } from '../utils/apollo-client';
import { NotFound } from './404';
import { LotDetail } from './lot-detail';
import { LotLeaderboard } from './lot-leaderboard';

export function Lot() {
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
  });
  const { lotId = '' } = useParams();
  const { data, loading, subscribeToMore } = useLotQuery({
    // fetchPolicy: 'cache-and-network',
    variables: { id: lotId },
    skip: lotId === '',
    // pollInterval: 1 * 60 * 1_000,
  });
  const lot = data?.Lot;

  useEffect(() => {
    trackProductViewed({ lotId });
    const unsubscribe = subscribeToMore({
      document: LiveLotFeedDocument,
      variables: { lotIds: [lotId] },
      updateQuery(prev, { subscriptionData }) {
        // @todo add bid into leaderboard and sort by amount
        if (!subscriptionData.data) {
          return prev;
        }

        const {
          liveLotFeed: { type, lot, bid },
        } = subscriptionData.data as unknown as LiveLotFeedSubscription;
        let leaderboard: LeaderboardRank[] = [...prev.Lot.leaderboard];
        let highestBids = [...prev.Lot.highestBids];
        let yourBid = prev.Lot.yourBid;

        if (bid) {
          // Find previous bid from the same paddle
          const previousBid = leaderboard.find(
            (n) => n.paddleId === bid.paddle.id
          );

          // Update leaderboard
          leaderboard = [
            {
              id: bid.id,
              amount: bid.amount,
              createdAt: bid.createdAt,
              paddleId: bid.paddle.id,
              paddleNumber: bid.paddle.number,
              rank: 1,
              totalBids: previousBid?.totalBids ? previousBid.totalBids + 1 : 1,
            },
            ...leaderboard,
          ]
            .filter((value, index, self) => {
              if (type === 'BidRemoved' && value.id === bid.id) {
                return false;
              }
              return index === self.findIndex((t) => t.id === value.id);
            })
            .sort((a, b) => b.amount - a.amount)
            .map((n, i) => ({
              ...n,
              rank: i + 1,
              __typename: 'LeaderboardRank',
            }));

          highestBids = leaderboard.map((rank) => ({
            __typename: 'Bid',
            id: rank.id,
            amount: rank.amount,
            paddleId: rank.paddleId,
            createdAt: rank.createdAt,
          }));

          if (me.data?.me) {
            // Find highest bid by you.
            const yourRank = leaderboard.find(
              (lr) => lr.paddleId === me.data?.me?.paddle?.id
            );
            if (yourRank) {
              yourBid = {
                __typename: 'Bid',
                id: yourRank.id,
                paddleId: yourRank.paddleId,
                amount: yourRank.amount,
                createdAt: yourRank.createdAt,
              };
            }

            // Notifications
            // if (Notification.permission === 'granted' && prev.Lot.yourBid) {
            //   if (!yourRank) {
            //     // You are off the leaderboard
            //     new Notification(
            //       'You are off the leaderboard for lot #' + lot?.number
            //     );
            //   } else {
            //     new Notification('New bid for lot #' + lot?.number);
            //   }
            // }
          }
        }

        leaderboard = leaderboard.slice(0, 10);

        if (lot) {
          const id = client.cache.identify({ __typename: 'Lot', id: lot?.id });
          // Maybe my bid?
          client.cache.modify({
            id,
            fields: {
              highestBids(previous) {
                return highestBids || previous;
              },
              yourBid(previous) {
                return yourBid || previous;
              },
              leaderboard(previous) {
                return leaderboard || previous;
              },
            },
          });
        }

        return Object.assign({}, prev, {
          Lot: {
            ...prev.Lot,
            ...lot,
            yourBid,
            highestBids,
            leaderboard,
          },
        });
      },
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId]);

  if (!loading && !lot) {
    return <NotFound />;
  }

  return (
    <Routes>
      <Route
        path="leaderboard/*"
        element={<LotLeaderboard lot={lot} loading={loading} />}
      />
      <Route
        path="*"
        element={
          <LotDetail
            lot={lot}
            loading={loading}
            auction={me?.data?.activeAuction}
          />
        }
      />
    </Routes>
  );
}
